import React from 'react';

// Top view of the car for the paint map, 800 x 400: front on the left, right side on top.
// Panels are tinted by their reading class (ok / warn / alert, or none when there is no reading).
const BODY =
    'M120,58 L680,58 C724,58 744,84 746,130 L746,270 C744,316 724,342 680,342 L120,342 C78,342 56,318 54,280 L54,120 C56,82 78,58 120,58 Z';

// x0..x1 / y0..y1 of each panel, clipped by the body outline; bumpers run the full width of the car
const PANEL_BOXES = {
    frontBumper: [30, 96, 30, 370],
    bonnet: [96, 236, 100, 300],
    roof: [300, 540, 100, 300],
    tailgate: [596, 704, 100, 300],
    rearBumper: [704, 770, 30, 370],
    frontWing: [96, 236],
    frontDoor: [236, 418],
    rearDoor: [418, 592],
    rearWing: [592, 704],
};
const SIDE_BAND = { Right: [30, 100], Left: [300, 370] };
const GLASS = [
    [236, 300],
    [540, 596],
];

const FILL = {
    ok: 'rgba(255,255,255,0.13)',
    warn: 'rgba(252,211,77,0.42)',
    alert: 'rgba(232,62,140,0.55)',
};
const SEAM = '#0D0B21';
const DETAIL = 'rgba(255,255,255,0.45)';
const LAMP = 'rgba(255,255,255,0.85)';
const RUBBER = '#2A2840';
const TRIM = 'rgba(255,255,255,0.22)';

const box = place => {
    const [x0, x1, y0, y1] = PANEL_BOXES[place.pos];
    const [b0, b1] = place.side === 'Centre' ? [y0, y1] : SIDE_BAND[place.side];
    return { x: x0, y: b0, width: x1 - x0, height: b1 - b0 };
};

const Line = ({ d, w = 1.5 }) => (
    <path d={d} fill="none" stroke={DETAIL} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
);

const TYRES = [
    [118, 40],
    [118, 340],
    [612, 40],
    [612, 340],
];

export const CarTopView = ({ places, id }) => (
    <>
        <defs>
            <clipPath id={`${id}-body`}>
                <path d={BODY} />
            </clipPath>
            <pattern id={`${id}-none`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="8" height="8" fill="rgba(255,255,255,0.03)" />
                <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.16)" strokeWidth="2" />
            </pattern>
            <linearGradient id={`${id}-glass`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="rgba(100,172,255,0.42)" />
                <stop offset="1" stopColor="rgba(100,172,255,0.14)" />
            </linearGradient>
        </defs>
        {TYRES.map(([x, y]) => (
            <g key={`${x}-${y}`}>
                <rect x={x} y={y} width="76" height="20" rx="7" fill={RUBBER} />
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <line
                        key={i}
                        x1={x + i * 11}
                        y1={y + 3}
                        x2={x + i * 11}
                        y2={y + 17}
                        stroke={SEAM}
                        strokeWidth="1.5"
                    />
                ))}
            </g>
        ))}
        <path
            d="M244,58 L250,38 Q254,31 263,31 L283,33 Q290,35 288,44 L280,58 Z"
            fill={TRIM}
            stroke="#FFFFFF"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <path
            d="M244,342 L250,362 Q254,369 263,369 L283,367 Q290,365 288,356 L280,342 Z"
            fill={TRIM}
            stroke="#FFFFFF"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <rect x="740" y="132" width="14" height="10" rx="4" fill={RUBBER} />
        <rect x="740" y="258" width="14" height="10" rx="4" fill={RUBBER} />
        <g clipPath={`url(#${id}-body)`}>
            {places.map(place => (
                <rect
                    key={place.key}
                    {...box(place)}
                    fill={place.cls === 'none' ? `url(#${id}-none)` : FILL[place.cls]}
                    stroke={SEAM}
                    strokeWidth="4"
                />
            ))}
            {GLASS.map(([x0, x1]) => (
                <rect
                    key={x0}
                    x={x0}
                    y="100"
                    width={x1 - x0}
                    height="200"
                    fill={`url(#${id}-glass)`}
                    stroke={SEAM}
                    strokeWidth="4"
                />
            ))}
            {/* bonnet creases and front edge, grille */}
            <Line d="M104,134 Q170,124 232,128" />
            <Line d="M104,266 Q170,276 232,272" />
            <Line d="M98,112 Q92,200 98,288" />
            <rect x="62" y="156" width="12" height="88" rx="5" fill="none" stroke={DETAIL} strokeWidth="1.5" />
            <Line d="M64,176 L72,176 M64,192 L72,192 M64,208 L72,208 M64,224 L72,224" w={1} />
            {/* headlights and fog lights */}
            <path d="M58,110 Q62,82 90,70 L96,98 Q76,104 72,116 Z" fill={LAMP} stroke="#FFFFFF" strokeWidth="1" />
            <path d="M58,290 Q62,318 90,330 L96,302 Q76,296 72,284 Z" fill={LAMP} stroke="#FFFFFF" strokeWidth="1" />
            <circle cx="66" cy="136" r="3.5" fill={LAMP} />
            <circle cx="66" cy="264" r="3.5" fill={LAMP} />
            {/* wipers */}
            <Line d="M246,262 L282,196" w={2} />
            <Line d="M246,190 L282,128" w={2} />
            <Line d="M556,200 L580,170" w={2} />
            {/* side glass along the roof edge, split by the B-pillar */}
            {[100, 284].map(y => (
                <g key={y}>
                    <path
                        d={`M304,${y} L414,${y} L414,${y + 16} L310,${y + 16} Z`}
                        fill={`url(#${id}-glass)`}
                        stroke={DETAIL}
                        strokeWidth="1"
                    />
                    <path
                        d={`M422,${y} L536,${y} L528,${y + 16} L422,${y + 16} Z`}
                        fill={`url(#${id}-glass)`}
                        stroke={DETAIL}
                        strokeWidth="1"
                    />
                </g>
            ))}
            {/* roof aerial */}
            <path
                d="M512,194 L532,197 L532,203 L512,206 Q506,200 512,194 Z"
                fill={TRIM}
                stroke={DETAIL}
                strokeWidth="1"
            />
            {/* door handles */}
            {[376, 550].map(x => (
                <g key={x}>
                    <rect x={x} y="66" width="20" height="6" rx="3" fill="none" stroke={DETAIL} strokeWidth="1.2" />
                    <rect x={x} y="328" width="20" height="6" rx="3" fill="none" stroke={DETAIL} strokeWidth="1.2" />
                </g>
            ))}
            {/* wheel arches, fuel cap */}
            <Line d="M112,60 Q156,70 200,60" />
            <Line d="M112,340 Q156,330 200,340" />
            <Line d="M606,60 Q650,70 694,60" />
            <Line d="M606,340 Q650,330 694,340" />
            <circle cx="676" cy="84" r="6" fill="none" stroke={DETAIL} strokeWidth="1.2" />
            {/* boot lid edge, spoiler lip, rear plate, tail lights */}
            <Line d="M600,112 Q596,200 600,288" />
            <Line d="M700,116 Q706,200 700,284" w={2.5} />
            <rect x="726" y="172" width="10" height="56" rx="3" fill="none" stroke={DETAIL} strokeWidth="1.5" />
            <path d="M744,114 Q740,82 714,70 L708,94 Q726,100 730,116 Z" fill="#E83E8C" />
            <path d="M744,286 Q740,318 714,330 L708,306 Q726,300 730,284 Z" fill="#E83E8C" />
        </g>
        <path d={BODY} fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinejoin="round" />
    </>
);
